import React, { useEffect, useRef, useState } from "react";
import type { TrialAnalysisResult } from "~/types/api";
import { ApiError, apiFetch, getCsrfToken } from "~/lib/api";
import { Button } from "~/components/ui/button";
import TrialResultsTeaser from "./TrialResultsTeaser";
import ResumeInput from "./ResumeInput";
import { trackEvent } from "~/lib/analytics";

type AppState = "idle" | "analyzing" | "done" | "error" | "exhausted";

const ANALYSIS_STAGES = ["Parsing resume...", "Matching skills...", "Scoring match..."];

const EXAMPLE_RESUME = `Alex Rivera
alex.rivera@email.com | linkedin.com/in/alexrivera | github.com/alexrivera

EXPERIENCE

Backend Engineer — FinTech Startup (2022–present)
- Built and maintained Python/Django REST APIs serving 50k daily active users
- Designed PostgreSQL schemas and wrote complex queries for reporting dashboards
- Containerized services with Docker; deployed on AWS EC2 with auto-scaling groups
- Reduced API p95 latency by 40% through query optimization and Redis caching

Junior Software Engineer — Agency (2020–2022)
- Developed React frontends consuming internal REST APIs
- Wrote unit and integration tests; maintained 85% coverage across core modules
- Participated in on-call rotation, resolving production incidents

SKILLS
Python, Django, JavaScript, React, PostgreSQL, Redis, Docker, AWS (EC2, S3, RDS), Git

EDUCATION
B.S. Computer Science — State University, 2020`;

const EXAMPLE_JD = `Senior Backend Engineer — Payments Platform

We're looking for a Senior Backend Engineer to join our Payments team. You'll design and scale the APIs that power millions of transactions daily.

Requirements:
- 5+ years of backend engineering experience
- Strong proficiency in Python or Go
- Deep experience with PostgreSQL and Redis in production
- Experience designing and operating distributed systems at scale
- Kubernetes and container orchestration in production
- AWS or GCP cloud infrastructure

Nice to have:
- Experience with Kafka or other message brokers
- GraphQL API design
- Terraform or other IaC tools

What you'll do:
- Own backend services end-to-end: design, build, deploy, and monitor
- Collaborate with product and frontend teams on API contracts
- Mentor junior engineers and lead technical discussions`;

const EXAMPLE_RESULT: import("~/types/api").TrialAnalysisResult = {
  match_score: 62,
  summary: "You're a 62% match but missing the two requirements this role weights most: Kubernetes and Go. Before applying, spin up a small Go service and deploy it on a local Kubernetes cluster — the JD mentions both five times as core requirements. The 5+ years requirement is a gap at 4 years total experience, so expect a direct question about scope and ownership.",
  verdict: "Apply — address Kubernetes and Go gaps in your cover letter",
  matched_preview: ["Python / Django", "PostgreSQL", "Redis"],
  critical_missing_preview: ["Kubernetes", "Go", "5+ years exp"],
  nice_to_have_preview: ["Kafka", "GraphQL", "Terraform"],
  total_matched: 7,
  total_critical_missing: 3,
  total_nice_to_have: 3,
};

const isTrialExhausted = (err: unknown) => err instanceof ApiError && err.code === "trial_exhausted";

function getContainerData() {
  const el = document.getElementById("trial-analyzer-app");
  return {
    googleAuthPath: el?.dataset.googleAuthPath ?? "/users/auth/google_oauth2",
  };
}

const TrialAnalyzerApp: React.FC = () => {
  const { googleAuthPath } = getContainerData();

  const [resumeText, setResumeText] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [appState, setAppState] = useState<AppState>("idle");
  const [eligibilityChecked, setEligibilityChecked] = useState(false);
  const [result, setResult] = useState<TrialAnalysisResult | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [stageIndex, setStageIndex] = useState(0);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  useEffect(() => {
    apiFetch("/api/v1/trial/analysis")
      .then(() => setEligibilityChecked(true))
      .catch((err) => {
        if (isTrialExhausted(err)) setAppState("exhausted");
        setEligibilityChecked(true);
      });

    return () => stopPolling();
  }, []);

  useEffect(() => {
    if (appState !== "analyzing") { setStageIndex(0); return; }
    const id = setInterval(() => setStageIndex((i) => (i + 1) % ANALYSIS_STAGES.length), 1800);
    return () => clearInterval(id);
  }, [appState]);

  useEffect(() => {
    if (appState === "exhausted") trackEvent("trial_exhausted_shown");
  }, [appState]);

  const performAnalysis = async (resume: string, jd: string, source: "manual" | "example") => {
    trackEvent("trial_started", { source });
    setAppState("analyzing");
    setErrorMessage(null);

    try {
      const { token } = await apiFetch<{ token: string; status: string }>("/api/v1/trial/analysis", {
        method: "POST",
        body: JSON.stringify({ resume_text: resume, job_description: jd }),
      });

      stopPolling();
      pollRef.current = setInterval(async () => {
        try {
          const data = await apiFetch<TrialAnalysisResult & { status: string; error?: string }>(
            `/api/v1/trial/analyses/${token}`
          );

          if (data.status === "complete") {
            stopPolling();
            setResult(data);
            setAppState("done");
            trackEvent("trial_complete", { match_score: data.match_score, source });
          } else if (data.status === "failed") {
            stopPolling();
            const message = data.error ?? "Analysis failed.";
            trackEvent("trial_error", { message });
            setErrorMessage(message);
            setAppState("error");
          }
        } catch (pollErr) {
          stopPolling();
          const message = pollErr instanceof Error ? pollErr.message : "Analysis failed.";
          trackEvent("trial_error", { message });
          setErrorMessage(message);
          setAppState("error");
        }
      }, 2000);
    } catch (err) {
      if (isTrialExhausted(err)) {
        setAppState("exhausted");
      } else {
        const message = err instanceof Error ? err.message : "Analysis failed.";
        trackEvent("trial_error", { message });
        setErrorMessage(message);
        setAppState("error");
      }
    }
  };

  const handleAnalyze = (e: React.FormEvent) => {
    e.preventDefault();
    if (!resumeText.trim() || !jobDescription.trim()) return;
    performAnalysis(resumeText, jobDescription, "manual");
  };

  const handleExample = async () => {
    trackEvent("trial_example_clicked");
    setResumeText(EXAMPLE_RESUME);
    setJobDescription(EXAMPLE_JD);
    setAppState("analyzing");
    setErrorMessage(null);
    await new Promise((resolve) => setTimeout(resolve, 3000));
    setResult(EXAMPLE_RESULT);
    setAppState("done");
    trackEvent("trial_complete", { match_score: EXAMPLE_RESULT.match_score, source: "example" });
  };

  const inputCls =
    "w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-none";

  const isAnalyzing = appState === "analyzing";

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-4 py-8 space-y-8">
        <header className="space-y-5">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Find out why you're not getting interviews</h1>
            <p className="text-muted-foreground mt-1">
              Paste any job description and get an AI match score, skill gaps, and a verdict in 10 seconds.
            </p>
          </div>
          {appState === "idle" && eligibilityChecked && (
            <div className="space-y-3">
              <button
                type="button"
                onClick={handleExample}
                className="w-full sm:w-auto rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 text-sm font-semibold transition-colors"
              >
                See it in action — no resume needed →
              </button>
              <p className="text-xs text-muted-foreground">Uses a sample resume · Free · No sign-up</p>
            </div>
          )}
        </header>

        {appState === "idle" && eligibilityChecked && (
          <div className="flex items-center gap-3">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs text-muted-foreground">or use your own resume</span>
            <div className="h-px flex-1 bg-border" />
          </div>
        )}

        {appState !== "done" && appState !== "exhausted" && (
          <form onSubmit={handleAnalyze} className="space-y-4">
            <ResumeInput
              value={resumeText}
              onChange={setResumeText}
              disabled={isAnalyzing}
            />
            <div>
              <label className="text-sm font-medium mb-1.5 block">Job description</label>
              <textarea
                value={jobDescription}
                onChange={(e) => setJobDescription(e.target.value)}
                placeholder="Paste the job description here..."
                rows={8}
                required
                className={inputCls}
                disabled={isAnalyzing}
              />
            </div>

            {appState === "error" && errorMessage && (
              <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 flex items-center justify-between">
                <p className="text-sm text-destructive">{errorMessage}</p>
                <button
                  type="button"
                  onClick={() => { setAppState("idle"); setErrorMessage(null); }}
                  className="text-xs text-destructive underline underline-offset-2 ml-4 hover:no-underline"
                >
                  Try again
                </button>
              </div>
            )}

            <div className="flex items-center gap-3">
              <Button
                type="submit"
                disabled={isAnalyzing || !eligibilityChecked || !resumeText.trim() || !jobDescription.trim()}
              >
                {isAnalyzing ? ANALYSIS_STAGES[stageIndex] : "Analyze Match"}
              </Button>
              {!eligibilityChecked && !isAnalyzing && (
                <span className="text-sm text-muted-foreground">Checking availability...</span>
              )}
              {isAnalyzing && (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                  <span className="text-sm text-muted-foreground">Usually takes 5–8 seconds...</span>
                </>
              )}
            </div>
          </form>
        )}

        {appState === "done" && result && (
          <>
            <button
              type="button"
              onClick={() => { setAppState("idle"); setResult(null); }}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              ← Analyze a different job
            </button>
            <TrialResultsTeaser result={result} googleAuthPath={googleAuthPath} />
          </>
        )}

        {appState === "exhausted" && (
          <div className="rounded-xl border bg-card p-8 text-center space-y-4">
            <p className="text-lg font-semibold">Want to analyze another job?</p>
            <p className="text-sm text-muted-foreground max-w-sm mx-auto">
              Sign up for free to run unlimited analyses, see full skill breakdowns, salary data, and track your applications.
            </p>
            <form method="post" action={googleAuthPath}>
              <input type="hidden" name="authenticity_token" value={getCsrfToken()} />
              <button
                type="submit"
                onClick={() => trackEvent("signup_cta_click", { source: "trial_exhausted" })}
                className="inline-flex items-center justify-center rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 text-sm font-semibold transition-colors cursor-pointer"
              >
                Sign up with Google — it's free
              </button>
            </form>
            <p className="text-xs text-muted-foreground">Free during beta · No credit card</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrialAnalyzerApp;
